import { SelectOptions } from 'components/commonType';
import { Modal } from 'components/Modal/Modal';
import Select from 'components/Select/Select';
import { useCallback, useEffect, useState } from 'react';
import i18n from 'i18n';
import { withFormikField } from 'hoc/withFormikField';
import { validateEmpty } from 'utils/ValidateUtils';
import Tips from 'components/Tips/Tips';
import { isEmpty } from 'lodash';
import classnames from 'classnames/bind';
import styles from './vendorNumberSelectField.module.scss';
import { ALL_VENDOR_OPTION } from 'core/ecSegment/ECSegment';

const cx = classnames.bind(styles);

export const VendorNumberSelectField: React.FC<{
  value?: string;
  error?: string;
  vendorNumberOptions: SelectOptions[];
  onChange: (value: string) => void;
}> = ({
  value,
  error,
  vendorNumberOptions,
  onChange
}) => {

  const [allVendorNumberOptions, setAllVendorNumberOptions] = useState<SelectOptions[]>([]);
  const [vendorNumberToSelect, setVendorNumberToSelect] = useState<string>();

  useEffect(() => {
    const validOptions = [ALL_VENDOR_OPTION, ...vendorNumberOptions];
    if (!value) {
      setAllVendorNumberOptions(validOptions);
      return;
    }

    const vendorNumberOption = validOptions.find(
      (option) => option.value === value
    );
    if (!vendorNumberOption) {
      setAllVendorNumberOptions([...validOptions, { label: value, value: value }]);
    } else {
      setAllVendorNumberOptions(validOptions);
    }

  }, [value, vendorNumberOptions]);

  const onConfirmSelectVendorNumber = useCallback(() => {
    if (vendorNumberToSelect) {
      onChange(vendorNumberToSelect);
      setVendorNumberToSelect(undefined);
    }
  }, [vendorNumberToSelect, onChange, setVendorNumberToSelect]);

  const onCloseSelectVendorNumber = useCallback(() => {
    setVendorNumberToSelect(undefined);
  }, [setVendorNumberToSelect]);

  const onSelectChange = useCallback((selectedValue: string) => {
    if (isEmpty(value)) {
      onChange(selectedValue);
      return;
    }
    setVendorNumberToSelect(selectedValue);
  }
  , [value, onChange, setVendorNumberToSelect]);

  const hasError = error !== undefined;
  const selectClassName = cx({
    error: hasError
  });

  return (
    <>
      {vendorNumberToSelect && (value !== vendorNumberToSelect) && (
        <Modal
          title={i18n.t<string>('common.titles.hint')}
          primaryButton={{
            title: i18n.t<string>('common.buttons.confirm'),
            callback: onConfirmSelectVendorNumber
          }}
          secondaryButton={{
            title: i18n.t<string>('common.buttons.cancel'),
            callback: onCloseSelectVendorNumber
          }}
          dismiss={onCloseSelectVendorNumber}
        >
          {i18n.t<string>('ecSegmentForm.labels.vendorNumberConfirm')}
        </Modal>
      )}
      <Select
        options={allVendorNumberOptions}
        name='permissionOptions'
        simpleValue
        value={value}
        className={selectClassName}
        onChange={onSelectChange}
      />
      <Tips>{error}</Tips>
    </>
  );
};

export type FormikVendorNumberSelectFieldProps = {
  name: string;
  vendorNumberOptions: SelectOptions[];
  validate?: (vendorNumber: string) => string | undefined;
  onChange?: (vendorNumber: string) => void;
};

export const FormikVendorNumberSelectField = withFormikField<FormikVendorNumberSelectFieldProps>(VendorNumberSelectField);

export const renderFormikVendorNumberSelectField = (
  name: string,
  vendorNumberOptions: SelectOptions[],
  onChange?: (vendorNumber: string) => void
) => {

  const validateVendorNumber = (value: string) => {
    const error = validateEmpty(value);
    if (error) {
      return error;
    }
    if (value === ALL_VENDOR_OPTION.value) {
      return undefined;
    }

    if (!vendorNumberOptions.find((option) => option.value === value)) {
      return i18n.t<string>('ecSegmentForm.errors.invalidVendorNumber');
    }
    return undefined;
  };

  return (
    <FormikVendorNumberSelectField
      name={name}
      vendorNumberOptions={vendorNumberOptions}
      validate={validateVendorNumber}
      onChange={onChange}
    />
  );
};
